// lib
import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

// components
import ImagePopup from '@aytm//lib/rfd-ui/components/basic/image-popup'

// style
import style from '@aytm/app/stats/components/image-preview/styles/ImagePreview.module.scss'

const ImagePreview = React.forwardRef(
  (
    {
      className,
      label,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      imageUrl,
      previewUrl: previewUrlProp,
      openImageOnZoomButtonOnly,
      doubleBuffering,
      allowScrolling,
      onPreviewLoaded,
      onImageOpen,
      onImageHide,
      onImageLoaded
    },
    ref
  ) => {
    const [previewUrl, setPreviewUrl] = useState(null)
    const [previewState, setPreviewState] = useState('fetching')

    const [showImagePopup, setShowImagePopup] = useState(false)

    const imagePreviewRef = useRef(null)

    const loadTimerRef = useRef(null)

    const onLoad = useCallback(() => {
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current)

      loadTimerRef.current = setTimeout(() => {
        if (typeof onImageLoaded === 'function') {
          onImageLoaded()
        }
      }, 10)
      setPreviewState('fetched')
    }, [onImageLoaded])

    const showImage = useCallback(() => {
      if (typeof onImageOpen === 'function') onImageOpen()
      setShowImagePopup(true)
    }, [onImageOpen])

    const hideImage = useCallback(() => {
      if (typeof onImageHide === 'function') onImageHide()
      setShowImagePopup(false)
    }, [onImageHide])

    const setPreviewImage = useCallback(
      url => {
        if (doubleBuffering === false) {
          setPreviewState('fetching')
          setPreviewUrl(url)
        } else {
          // Clean up previous image element if exists
          if (imagePreviewRef.current) {
            imagePreviewRef.current.remove()
            imagePreviewRef.current = null
          }

          const buffer = document.createElement('img')
          imagePreviewRef.current = buffer
          buffer.addEventListener('load', () => {
            // removing a detached image does not stop its load, so one that finishes after it was
            // replaced, or after the preview unmounted, is not this preview's to report
            if (imagePreviewRef.current !== buffer) return

            if (typeof onPreviewLoaded === 'function') {
              onPreviewLoaded(url)
            }

            setPreviewState('fetched')
            setPreviewUrl(url)

            imagePreviewRef.current?.remove()
            imagePreviewRef.current = null
          })
          setPreviewState('fetching')
          imagePreviewRef.current.src = url
        }
      },
      [doubleBuffering, onPreviewLoaded]
    )

    useEffect(() => setPreviewImage(previewUrlProp), [previewUrlProp, setPreviewImage])

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (loadTimerRef.current) clearTimeout(loadTimerRef.current)
        if (imagePreviewRef.current) {
          imagePreviewRef.current.remove()
          imagePreviewRef.current = null
        }
      }
    }, [])

    return (
      <div
        ref={ref}
        className={classNames(style.component, {
          [className]: Boolean(className),
          [style.loading]: 'fetched' != previewState
        })}
        style={{ minWidth, maxWidth, minHeight, maxHeight }}
      >
        <div
          className={classNames(style.image, {
            [style['image-fetched']]: 'fetched' == previewState,
            [style['image-inactive']]: openImageOnZoomButtonOnly
          })}
          style={{ minWidth }}
          onClick={openImageOnZoomButtonOnly ? null : showImage}
        >
          <div className={style['image-box']} style={{ minWidth, maxWidth, maxHeight }}>
            {previewUrl ? <img src={previewUrl} alt="" style={{ maxWidth, maxHeight }} onLoad={onLoad} /> : null}
            {'fetching' == previewState ? (
              <div className={style['image-loader']}>
                <i className="icon-aytmfd-spin aytmfd-spin" />
              </div>
            ) : null}
            {'fetched' == previewState ? (
              <i
                data-testid="stat-image-preview-zoom"
                className={`${style['image-zoom']} icon-aytmfd-zoom-in`}
                onClick={openImageOnZoomButtonOnly ? showImage : null}
              />
            ) : null}
          </div>
        </div>
        {previewUrl && imageUrl ? (
          <ImagePopup
            src={imageUrl}
            label={label}
            show={showImagePopup}
            scrolling={allowScrolling}
            closeOnClick
            onClose={hideImage}
          />
        ) : null}
      </div>
    )
  }
)

ImagePreview.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  minWidth: PropTypes.string,
  maxWidth: PropTypes.string,
  minHeight: PropTypes.string,
  maxHeight: PropTypes.string,
  imageUrl: PropTypes.string,
  previewUrl: PropTypes.string,
  openImageOnZoomButtonOnly: PropTypes.bool,
  doubleBuffering: PropTypes.bool,
  allowScrolling: PropTypes.bool,
  onPreviewLoaded: PropTypes.func,
  onImageOpen: PropTypes.func,
  onImageHide: PropTypes.func,
  onImageLoaded: PropTypes.func
}

export default React.memo(ImagePreview)
